<template>
  <v-container>
    <ApolloQuery
      :query="isLocalBook ? localBookQuery : bookQuery"
      :variables="queryVariables"
      :update="isLocalBook ? updateLocalBook : updateBook"
    >
      <template #default="{ result: { error, data }, isLoading }">
        <v-container class="pa-0">
          <apollo-error-alert
            v-if="error && data"
            :error="error"
          />
          <BookInfoLoader v-if="isLoading" />
          <not-found v-else-if="!data" />
          <div v-else>
            <div class="d-flex">
              <div class="left-column pa-0">
                <book-cover-enlargeable
                  :src="data.imageUrl"
                  :title="data.title"
                />
              </div>
              <div
                class="d-flex flex-column justify-start pa-0 py-sm-2 py-md-4 pl-3 pl-sm-5 pl-md-11"
              >
                <login-to-view-modal :should-show="shouldShowLoginToViewModal" />
                <div>
                  <h1 class="text-subtitle-1 text-md-h4 font-weight-bold">
                    {{ decodeHtml(data.title) }}
                  </h1>
                  <div v-if="displayAuthors.length > 0">
                    <h2 class="text-subtitle-2 text-md-h5 font-weight-regular">
                      <span>
                        {{ $t("bookInfo.by") }}
                      </span>
                      <span
                        v-for="(entry, index) in displayAuthors"
                        :key="index"
                      >
                        <router-link
                          v-if="entry.id"
                          :to="{ name: 'author', params: { id: entry.id } }"
                          class="author-link"
                        >{{ decodeHtml(entry.name) }}</router-link>
                        <template v-else>{{ decodeHtml(entry.name) }}</template><template v-if="index + 1 < displayAuthors.length">, </template>
                      </span>
                    </h2>
                  </div>
                  <h2
                    v-else
                    class="book-info__author"
                  >
                    {{ $t("bookInfo.authorUnknown") }}
                  </h2>
                  <add-to-book-list-modal
                    v-if="isLoggedIn"
                    class="my-2"
                    :book-id="data._id"
                  />
                </div>
                <format-selector
                  :book="data"
                  :is-local-book="isLocalBook"
                />
                <div>
                  <price
                    v-if="data.currency && (!ownedBook || data?.format._id === 'print')"
                    class="text-subtitle-1 text-md-h5 mb-2"
                    :book-price="Number(data.price)"
                    :base-currency="data.currency._id"
                    :add-tax="!data.vatIncluded"
                    :should-round="true"
                    :skip-base-rounding="data.vatIncluded || false"
                  />
                  <div
                    v-if="data?.audioPreviewUrl && hasPreviewUrl"
                    class="my-3"
                  >
                    <audio-button :audio-url="data.audioPreviewUrl" />
                  </div>
                  <open-book-button
                    v-if="ownedBook && !isPrintBook(data.format)"
                    class="text-subtitle-2"
                    :read-url="ownedBook.readUrl"
                    :format="data?.format"
                    :book-id="data._id"
                    height="36px"
                    :min-width="buttonWidth"
                    :block="$vuetify.breakpoint.smAndDown"
                    rounded
                  />
                  <add-to-library-button
                    v-else-if="!ownedBook && (!data.currency || data.price == 0)"
                    button-classes="text-subtitle-2"
                    :product-id="data._id"
                    :query-loading="bookshelfLoading"
                    height="36px"
                    :min-width="buttonWidth"
                    :block="$vuetify.breakpoint.smAndDown"
                    rounded
                    :refetch-bookshelf="refetchBookshelf"
                    :short-text="$vuetify.breakpoint.smAndDown"
                  />
                  <div
                    v-else-if="isPrintBook(data.format)"
                    class="d-flex"
                    :class="$vuetify.breakpoint.smAndDown ? 'flex-column' : 'flex-row'"
                  >
                    <add-to-cart-button
                      :id="data.id"
                      :book="data"
                      button-classes="text-subtitle-2"
                      :simplified-id="data._id"
                      :query-loading="false"
                      height="36px"
                      :min-width="buttonWidth"
                      :block="$vuetify.breakpoint.smAndDown"
                      rounded
                      :class="$vuetify.breakpoint.smAndDown ? 'mb-2' : 'mr-2'"
                    />
                    <add-to-cart-button
                      v-if="data?.format?._id === 'print' && data.currency && data.price > 0"
                      :id="data.id"
                      :book="data"
                      :simplified-id="data._id"
                      :query-loading="false"
                      buy-now
                      button-classes="info-buy-now-btn"
                      height="36px"
                      :min-width="buttonWidth"
                      :block="$vuetify.breakpoint.smAndDown"
                      rounded
                      :class="$vuetify.breakpoint.smAndDown ? '' : 'ml-2'"
                      :add-to-cart-text="$t('shop.buyNow')"
                      icon="mdi-flash"
                      color1="formatPrint"
                      color2="formatPrint"
                    />
                  </div>
                  <add-to-cart-button
                    v-else
                    :id="data.id"
                    :book="data"
                    button-classes="text-subtitle-2"
                    :simplified-id="data._id"
                    :query-loading="bookshelfLoading"
                    height="36px"
                    :min-width="buttonWidth"
                    :block="$vuetify.breakpoint.smAndDown"
                    rounded
                  />
                </div>
              </div>
            </div>
            <div class="pt-5">
              <product-tabs :book="data" />
              <!-- Related Books section hidden - GitHub issue #1253 -->
              <!-- <div class="d-flex flex-column flex-grow-1 overflow-hidden pl-0 mx-n3 mx-md-0 pl-md-11">
                <ScreenViewHeader
                  class="mx-n3 mx-md-0 px-5 px-md-0"
                  :title="$t('shop.relatedBooks')"
                />
                <related-book-list
                  class="px-3 px-md-0"
                  :book-id="$route.params.id"
                  :tag-ids="data.tagIds"
                  :number-to-show="12"
                />
              </div> -->
            </div>
          </div>
        </v-container>
      </template>
    </ApolloQuery>
  </v-container>
</template>

<script>
import ProductTabs from "@/components/ProductTabs";
import AddToCartButton from "@/views/checkout/AddToCartButton";
import Price from "@/components/Price";
import { decodeHtml } from "@/helpers/generalFunctions";
import {
  generateBookSchema,
  generateProductSchema,
  generateBreadcrumbSchema,
  injectJsonLd,
} from "@/helpers/jsonLdHelper";
import ApolloErrorAlert from "@/components/ApolloErrorAlert";
import BookCoverEnlargeable from "@/components/BookCoverEnlargeable";
import AddToLibraryButton from "@/components/AddToLibraryButton";
import OpenBookButton from "@/components/OpenBookButton";
import AddToBookListModal from "@/components/AddToBookListModal";
import LoginToViewModal from "@/components/LoginToViewModal";
import AudioButton from "@/components/AudioButton";
import BookInfoLoader from "@/views/skeletonLoaders/BookInfoLoader.vue";
import NotFound from "@/views/NotFound.vue";
import FormatSelector from "@/components/FormatSelector.vue";
import BookshelfMixin from "@/mixins/BookshelfMixin";
import { isPrintFormat } from "@/utils/BookFormatHelper";
import { buildSocialMeta, PRODUCTION_DOMAIN } from "@/helpers/socialMetaHelper";
import { buildLocalizedPageUrl } from "@/helpers/localeUrlHelper";
import gql from "graphql-tag";
import BookFragments from "@/graphql/BookFragments";
import LocalBookFragments from "@/graphql/LocalBookFragments";
import { buildBookUrl } from "@/helpers/slugHelper";
import { META_DESCRIPTION_MAX_LENGTH } from "@/utils/Const";

var images = require.context("@/assets/img/", false, /\.jpeg$/);

export default {
  components: {
    ProductTabs,
    AddToCartButton,
    Price,
    ApolloErrorAlert,
    BookCoverEnlargeable,
    AddToLibraryButton,
    OpenBookButton,
    AddToBookListModal,
    LoginToViewModal,
    AudioButton,
    BookInfoLoader,
    NotFound,
    FormatSelector,
  },
  mixins: [BookshelfMixin],
  data() {
    return {
      bookData: null,
      hasPreviewUrl: false,
      bookTagIds: [],
    };
  },
  metaInfo() {
    const url = `${PRODUCTION_DOMAIN}${buildLocalizedPageUrl(this.$route.path, this.$route.query?.lang, this.$store?.getters?.languageCode)}`
    const image = this.bookData?.imageUrl || undefined

    return {
      title: this.pageTitle,
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.pageDescription,
        },
        ...buildSocialMeta(
          this.pageTitle,
          this.pageDescription,
          url,
          image,
          'book',
        ),
        ...(this.bookData?.isbn ? [{ vmid: 'book:isbn', property: 'book:isbn', content: this.bookData.isbn }] : []),
        ...(this.bookData?.authors?.length ? this.bookData.authors.map(a => ({ property: 'book:author', content: a })) : []),
        ...(this.bookData?.publishedOn || this.bookData?.modified ? [{ vmid: 'book:release_date', property: 'book:release_date', content: this.bookData.publishedOn || this.bookData.modified }] : []),
        ...(this.bookData?.price != null && this.bookData?.currency?._id ? [
          { vmid: 'product:price:amount', property: 'product:price:amount', content: String(this.bookData.price) },
          { vmid: 'product:price:currency', property: 'product:price:currency', content: this.bookData.currency._id },
        ] : []),
      ],
    }
  },
  computed: {
    displayAuthors() {
      if (!this.bookData) return [];
      if (this.bookData.authorRefs?.length) return this.bookData.authorRefs;
      return (this.bookData.authors || []).map(name => ({ id: null, name }));
    },
    pageTitle() {
      if (!this.bookData) return this.$t('routes.book')
      const title = decodeHtml(this.bookData.title)
      const authors = this.bookData.authors
      if (authors && authors.length > 0) {
        return `${title} - ${authors.map(a => decodeHtml(a)).join(', ')}`
      }
      return title
    },
    pageDescription() {
      if (!this.bookData) return this.$t('seo.defaultDescription')
      if (this.bookData.description) {
        const text = decodeHtml(this.bookData.description).replace(/<[^>]*>/g, '')
        return text.length > META_DESCRIPTION_MAX_LENGTH ? text.substring(0, META_DESCRIPTION_MAX_LENGTH - 3) + '...' : text
      }
      return this.$t('seo.bookFallbackDescription', { title: decodeHtml(this.bookData.title) })
    },
    isSlugRoute() {
      return this.$route.name === 'bookBySlug' || this.$route.name === 'localBookBySlug';
    },
    bookId() {
      if (this.isSlugRoute) {
        const slug = this.$route.params.slug;
        // Legacy encoded IRI URLs (/book/%2Fbooks%2F123) decode to a slug
        // starting with '/'. Use the decoded value directly as the IRI.
        if (slug.startsWith('/books/') || slug.startsWith('/local_books/')) {
          return slug;
        }
        return this.$route.name === 'localBookBySlug'
          ? `/local_books/${slug}`
          : `/books/${slug}`;
      }
      return this.$route.params.id;
    },
    isLocalBook() {
      if (this.$route.name === 'localBookBySlug') return true;
      if (this.isSlugRoute) {
        return this.$route.params.slug.startsWith('/local_books/');
      }
      return this.bookId && this.bookId.includes('local_books');
    },
    isCleanSlugRoute() {
      return this.isSlugRoute && !this.$route.params.slug?.startsWith('/');
    },
    queryVariables() {
      if (this.isCleanSlugRoute) return { slug: this.$route.params.slug };
      return { id: this.bookId };
    },
    bookQuery() {
      if (this.isCleanSlugRoute) {
        return gql`
          query getBookBySlug($slug: String!) {
            bookBySlugBook(slug: $slug) {
              ...BookDetails
              audioPreviewUrl
              modified
              publisher
              language
            }
          }
          ${BookFragments.bookDetailsFragment}
        `;
      }
      return gql`
        query getBookById($id: ID!) {
          book(id: $id) {
            ...BookDetails
            audioPreviewUrl
            modified
            publisher
            language
          }
        }
        ${BookFragments.bookDetailsFragment}
      `;
    },
    localBookQuery() {
      if (this.isCleanSlugRoute) {
        return gql`
          query getLocalBookBySlug($slug: String!) {
            localBookBySlugLocalBook(slug: $slug) {
              ...LocalBookDetails
            }
          }
          ${LocalBookFragments.localBookDetailsFragment}
        `;
      }
      return gql`
        query getLocalBookById($id: ID!) {
          localBook(id: $id) {
            ...LocalBookDetails
          }
        }
        ${LocalBookFragments.localBookDetailsFragment}
      `;
    },
    ownedBook() {
      return this.checkInBookshelf(this.bookData?.id || this.bookId);
    },
    isLoggedIn() {
      return this.$auth.isAuthenticated;
    },
    shouldShowLoginToViewModal() {
      return Boolean(this.$route.params.fromEmail || this.$route.query.fromEmail);
    },
    buttonWidth() {
      return this.$vuetify.breakpoint.smAndDown ? "fit-content" : "200px";
    },
    bookJsonLd() {
      return generateBookSchema(this.bookData);
    },
    productJsonLd() {
      if (!this.bookData) return null;
      const price = this.bookData.price;
      const currencyCode = this.bookData.currency?._id;
      if (price == null || !currencyCode) return null;
      return generateProductSchema(this.bookData, { price, currencyCode });
    },
    breadcrumbJsonLd() {
      const origin = window.location.origin;
      return generateBreadcrumbSchema([
        { text: this.$t('routes.home'), href: origin },
        { text: this.pageTitle },
      ]);
    },
  },
  watch: {
    bookJsonLd: {
      immediate: true,
      handler(schema) { injectJsonLd('book', schema); },
    },
    productJsonLd: {
      immediate: true,
      handler(schema) { injectJsonLd('product', schema); },
    },
    breadcrumbJsonLd: {
      immediate: true,
      handler(schema) { injectJsonLd('breadcrumb', schema); },
    },
  },
  beforeDestroy() {
    injectJsonLd('book', null);
    injectJsonLd('product', null);
    injectJsonLd('breadcrumb', null);
  },
  methods: {
    updateBook(data) {
      const book = data.bookBySlugBook || data.book;
      if (!book) {
        this.bookData = null;
        return null;
      }
      this.checkUrlExists(book?.audioPreviewUrl);
      this.bookData = book;
      this.redirectToSlugUrl(book);
      return book;
    },
    updateLocalBook(data) {
      const localBook = data.localBookBySlugLocalBook || data.localBook;
      if (!localBook) {
        this.bookData = null;
        return null;
      }
      const sortedAuthors = localBook.bookAuthors
        ? [...localBook.bookAuthors].sort((a, b) => a.sequence - b.sequence)
        : [];
      const transformed = {
        ...localBook,
        authors: sortedAuthors.map(ba => ba.author.name),
        authorRefs: sortedAuthors.map(ba => ({ id: ba.author._id, name: ba.author.name })),
        thumbnailUrl: localBook.imageUrl,
      };
      this.bookData = transformed;
      this.redirectToSlugUrl(transformed);
      return transformed;
    },
    redirectToSlugUrl(book) {
      if (!book || this.isSlugRoute) return;

      const slugUrl = buildBookUrl(book, {
        fromEmail: this.$route.params.fromEmail,
        query: this.$route.query,
        hash: this.$route.hash,
      });
      if (!slugUrl || slugUrl === '/book/') return;

      const resolvedSlugRoute = this.$router.resolve(slugUrl).route;
      if (resolvedSlugRoute.fullPath !== this.$route.fullPath) {
        this.$router.replace(slugUrl);
      }
    },
    isPrintBook(format) {
      return isPrintFormat(format);
    },
    imgUrl: function (path) {
      return images("./" + path);
    },
    decodeHtml: function (html) {
      return decodeHtml(html);
    },
    async checkUrlExists(url) {
      if (!url) return;
      try {
        const response = await fetch(url, { method: "HEAD" });
        if (response.ok) {
          this.hasPreviewUrl = true;
        }
      } catch {
        // Audio preview URL not available
      }
    },
    handleFormatSelect(bookId) {
      const url = buildBookUrl(bookId);
      if (url && url !== this.$route.path) {
        this.$router.push(url);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.author-link {
  /* Match the publisher link (BookDetails.vue) — theme primary (red), no
     underline until hover. font: inherit keeps the heading's size/weight. */
  color: var(--v-primary-base);
  font: inherit;
  text-decoration: none;
}
.author-link:hover {
  text-decoration: underline;
}

::v-deep .left-column {
  min-width: 250px;
  width: 250px;
}

::v-deep .header-border {
  border-top: none;
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  ::v-deep .left-column {
    min-width: 148px;
    width: 148px;
  }

  ::v-deep .details-column {
    width: 100%;
    max-width: 100%;
  }
}
</style>
